import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Copy } from 'lucide-react';
import MedicationGroupByRoute, { createEmptyMedication } from './MedicationGroupByRoute';

export default function EditPrescriptionModal({ open, onClose, patient, prescription, onSuccess }) {
  const [prescriptionName, setPrescriptionName] = useState('');
  const [medicationGroups, setMedicationGroups] = useState([]);
  const [loading, setSaving] = useState(false);

  useEffect(() => {
    if (prescription && open) {
      setPrescriptionName(prescription.name || '');
      
      // Rebuild groups from existing data
      if (prescription.medication_groups && prescription.medication_groups.length > 0) {
        setMedicationGroups(prescription.medication_groups.map(g => ({
          via: g.via.replace(/^USO\s+/i, '').toUpperCase(),
          medicamentos: g.medicamentos.map(m => ({
            name: m.name || '',
            dosage: m.dosage || '',
            dosage_unit: m.dosage_unit || 'MG',
            packaging_type: m.packaging_type || m.dosage_type || 'comprimidos',
            quantity: m.quantity || '',
            instructions: m.instructions || ''
          }))
        })));
      } else {
        // Fallback: group by via from uso field
        const viaMatch = (prescription.uso || 'USO ORAL').match(/USO\s+(\S+)/i);
        const via = viaMatch ? viaMatch[1].toUpperCase() : 'ORAL';
        setMedicationGroups([{
          via,
          medicamentos: (prescription.medicamentos || []).map(m => ({
            name: m.name || '',
            dosage: m.dosage || '',
            dosage_unit: m.dosage_unit || 'MG',
            packaging_type: m.packaging_type || m.dosage_type || 'comprimidos',
            quantity: m.quantity || '',
            instructions: m.instructions || ''
          }))
        }]);
      }
    }
  }, [prescription, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Flatten all medications from groups
    const allMeds = [];
    const validGroups = [];
    
    for (const group of medicationGroups) {
      const validMeds = group.medicamentos.filter(m => m.name.trim() !== '');
      if (validMeds.length > 0) {
        validGroups.push({
          via: `USO ${group.via}`,
          medicamentos: validMeds
        });
        validMeds.forEach(m => allMeds.push({ ...m, via: `USO ${group.via}` }));
      }
    }
    
    if (allMeds.length === 0) {
      toast.error('Adicione pelo menos um medicamento');
      return;
    }

    const uso = validGroups.length === 1 
      ? validGroups[0].via 
      : validGroups.map(g => g.via).join(' / ');

    setSaving(true);
    try {
      await axiosInstance.put(`/prescriptions/${prescription.id}`, {
        medicamentos: allMeds,
        medication_groups: validGroups,
        uso,
        name: prescriptionName
      });
      onSuccess();
    } catch (error) {
      console.error('Erro ao atualizar receita:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar receita');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    // Flatten medications
    const allMeds = [];
    const validGroups = [];
    for (const group of medicationGroups) {
      const validMeds = group.medicamentos.filter(m => m.name.trim() !== '');
      if (validMeds.length > 0) {
        validGroups.push({ via: `USO ${group.via}`, medicamentos: validMeds });
        validMeds.forEach(m => allMeds.push({ ...m, via: `USO ${group.via}` }));
      }
    }
    
    if (allMeds.length === 0) {
      toast.error('Adicione pelo menos um medicamento');
      return;
    }

    const uso = validGroups.length === 1 
      ? validGroups[0].via 
      : validGroups.map(g => g.via).join(' / ');

    setSaving(true);
    try {
      await axiosInstance.post('/prescriptions/', {
        patient_id: patient.id,
        medicamentos: allMeds,
        medication_groups: validGroups,
        uso,
        name: prescriptionName ? `${prescriptionName} (copia)` : 'Receita (copia)'
      });
      toast.success('Receita duplicada com sucesso!');
      onSuccess();
    } catch (error) {
      console.error('Erro ao duplicar receita:', error);
      toast.error('Erro ao duplicar receita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Editar Receita - {patient?.nome}</DialogTitle>
          <DialogDescription>
            Atualize os medicamentos e instrucoes da receita. As alteracoes serao salvas diretamente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nome da Receita */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Nome da Receita (opcional)
            </Label>
            <Input
              value={prescriptionName}
              onChange={(e) => setPrescriptionName(e.target.value)}
              placeholder="Ex: Medicacao pos-operatoria, Tratamento cronico..."
              className="h-12"
              data-testid="edit-rx-prescription-name"
            />
          </div>

          {/* Medication Groups - same component as creation */}
          <MedicationGroupByRoute
            groups={medicationGroups}
            onChange={setMedicationGroups}
          />

          <div className="flex gap-3 justify-between border-t pt-4">
            <Button
              type="button"
              onClick={handleDuplicate}
              variant="outline"
              className="rounded-full px-6"
              disabled={loading}
              data-testid="duplicate-prescription-button"
            >
              <Copy className="w-4 h-4 mr-2" />
              Duplicar Receita
            </Button>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="rounded-full px-6"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="submit-edit-prescription-button"
                disabled={loading}
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-6"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvando...' : 'Salvar Alteracoes'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
